/**
 * SimpleAnalyticsPanel - シンプル分析パネルコンポーネント
 *
 * このコンポーネントは以下の機能を提供します：
 * - 期間、色、キーワードによるカレンダーイベントの分析
 * - ドーナツチャートと縦棒グラフによる可視化
 * - 有料プランユーザー向けの前期比較機能
 * - レスポンシブデザイン対応
 * - ETagキャッシュによる効率的なデータ取得
 */

import { ReactElement, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getEnglishHoursAndMinutes,
    getHoursAndMinutes,
    getWeekday,
    removeYearFromDate,
    removeZeroPaddingFromDate,
} from '@/utils/time';
import DonutChart from '@/features/charts/donutChart/DonutChart';
import NormalVerticalBarChart from '@/features/charts/normalVerticalBarChart/NormalvVerticalBarChart';
import StackedVerticalBarChart from '@/features/charts/stackedVerticalBarChart/StackedVerticalBarChart';
import type {
    StackedVerticalBarChartData,
    StackedVerticalBarChartDataset,
    StackedVerticalBarChartTooltipLabelFormatter,
} from '@/features/charts/stackedVerticalBarChart/util/StackedVerticalBarChart';
import { RatioEvents, TransitionEvents } from '@/types/AnalysisResultType';
import {
    SimpleAnalyticsResponse,
    SimpleAnalyticsUpdatePayload,
    SimpleAnalyticsViewModel,
} from '@/types/SimpleAnalytics';
import { fetchSimpleAnalytics, updateSimpleAnalytics } from '@/services/simpleAnalyticsService';
import { usePanelLoading } from '@/components/PanelLoadingBoundary';
import { useSmUpViewport } from '@/hooks/useSmUpViewport';
import Modal from '@/components/Modal';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
// import EditIcon from '@/assets/images/common/edit.png';
import HoverMenuButton from '@/components/menu/HoverMenuButton';
import { buildPanelMenuItems } from '@/components/menu/panelMenu';
import type { PanelMenuItem } from '@/components/menu/types';
import {
    buildBarChartViewModeMenuItem,
    type BarChartViewMode,
} from '@/features/charts/BarChartViewModeToggle';
import {
    getChartDisplayConfig,
} from '@/features/charts/useBarChartScrollContainer';
import Select from '@/components/Select';
import { LocalStorageKeys } from '@/constants/localStorage';
import { getLocalStorage, setLocalStorage } from '@/utils/localStrage';
import { DEFAULT_SIMPLE_ANALYTICS_COLOR, getPeriodDefinition, getPeriodOptions } from './constants';
import PeriodComparison from '@/components/PeriodComparison';
import { calculatePeriodComparison } from '@/utils/periodComparison';
import {
    getBaseColorOptionsByProvider,
    applyColorOrderWithDividers,
    getDefaultColorOption,
} from '@/utils/colorOptions';
import { useResponsiveFontSize } from '@/hooks/useResponsiveFontSize';
import { useUser } from '@/hooks/UserContext';
import { buildLanguageTagFromUser } from '@/utils/locale';
import { resolveWeekStart } from '@/i18n/weekStart';
import { getColors } from '@/services/colorService';
import { LegendIcon, DonutIcon, StackedIcon } from '@/components/icons/AnalyticsIcons';
import { EditIcon } from '@/components/icons/EditIcon';
import { FullscreenLockedModal } from '@/components/FullscreenLockedModal';
import { openPlansWithTracking } from '@/utils/openPlansWithTracking';
import { logSimpleAnalysisEvent } from '@/utils/customAnalysisEventLog';
import { apiGetRequest } from '@/utils/apiClient';
import {
    IndividualCalendarItem,
    createIndividualCalendarOptions,
    getDefaultIndividualCalendarOption,
    isHolidayOrBirthdayCalendar,
} from '@/features/analysis/seach/util/Search';
import { ExternalLinkIcon } from '@/components/icons/ExternalLinkIcon';

// 空のデータ用のデフォルト値
const EMPTY_RATIO: RatioEvents = {
    ratioEventItems: [],
    totalTime: 0,
    averageTime: 0,
    totalCount: 0,
    dailyAggregatedData: [],
};

const EMPTY_TRANSITION: TransitionEvents = {
    transitionEventItems: [],
    totalTime: 0,
    activeDays: 0,
    periodDays: 0,
    activeDaysRatio: 0,
    averageTimePerDay: 0,
};

// 比率データをマッピングする
const mapRatioResponse = (ratio?: SimpleAnalyticsResponse['ratio']): RatioEvents => {
    if (!ratio) {
        return { ...EMPTY_RATIO };
    }
    const list = Array.isArray(ratio.aggregatedDataByEventList)
        ? ratio.aggregatedDataByEventList.map((item: any) => ({
              title: item?.title ?? '',
              totalTime: Number(item?.totalTime ?? 0),
              percentage: Number(item?.percentage ?? 0),
              count: Number(item?.count ?? 0),
              averageTimePerCount: Number(item?.averageTimePerCount ?? 0),
              averageTimePerDay: Number(item?.averageTimePerDay ?? 0),
          }))
        : [];

    const daily = Array.isArray(ratio.dailyAggregatedData)
        ? ratio.dailyAggregatedData.map((item: any) => ({
              date: item?.date ?? '',
              totalTime: Number(item?.totalTime ?? 0),
              eventItems: Array.isArray(item?.eventItems)
                  ? item.eventItems.map((dailyItem: any) => ({
                        title: dailyItem?.title ?? '',
                        totalTime: Number(dailyItem?.totalTime ?? 0),
                        percentage: Number(dailyItem?.percentage ?? 0),
                    }))
                  : [],
          }))
        : [];

    return {
        ratioEventItems: list,
        totalTime: Number(ratio.totalTime ?? 0),
        averageTime: Number(ratio.averageTime ?? 0),
        totalCount: Number(ratio.totalCount ?? 0),
        dailyAggregatedData: daily,
    };
};

// 推移データをマッピングする
const mapTransitionResponse = (
    transition?: SimpleAnalyticsResponse['transition'],
): TransitionEvents => {
    if (!transition) {
        return { ...EMPTY_TRANSITION };
    }
    const list = Array.isArray(transition.dateAndTimeList)
        ? transition.dateAndTimeList.map((item: any) => ({
              date: item?.date ?? '',
              time: Number(item?.time ?? 0),
          }))
        : [];

    return {
        transitionEventItems: list,
        totalTime: Number(transition.totalTime ?? 0),
        activeDays: Number(transition.activeDays ?? 0),
        periodDays: Number(transition.periodDays ?? 0),
        activeDaysRatio: Number(transition.activeDaysRatio ?? 0),
        averageTimePerDay: Number(transition.averageTimePerDay ?? 0),
    };
};

// ビューモデルを構築する
const buildViewModel = (response: SimpleAnalyticsResponse): SimpleAnalyticsViewModel => {
    const chartType = response.chartType === 'transition' ? 'transition' : 'ratio';
    return {
        setting: {
            id: response.setting?.id,
            calendarId: response.setting?.calendarId ?? null,
            periodType: response.setting?.periodType ?? 'PAST_14_DAYS',
            // 初回は色未指定（空文字）で扱う
            color: response.setting?.color ?? '',
            keyword: response.setting?.keyword ?? '',
        },
        chartType,
        totalMinutes: Number(response.totalMinutes ?? 0),
        previousPeriodTotalMinutes: Number(response.previousPeriodTotalMinutes ?? 0),
        previousTransition: response.previousTransition,
        ratioData: chartType === 'ratio' ? mapRatioResponse(response.ratio) : undefined,
        transitionData:
            chartType === 'transition' ? mapTransitionResponse(response.transition) : undefined,
    };
};

// フォームの状態を管理する型定義
type FormState = {
    periodType: string;
    color: string;
    keyword: string;
};

/**
 * SimpleAnalyticsPanel メインコンポーネント
 * @returns SimpleAnalyticsPanelのReactElement
 */
type SimpleAnalyticsPanelProps = {
    onEnterFullScreen?: () => void;
    onExitFullScreen?: () => void;
    isFullScreen?: boolean;
};

const SimpleAnalyticsPanel = ({
    onEnterFullScreen,
    onExitFullScreen,
    isFullScreen = false,
}: SimpleAnalyticsPanelProps): ReactElement => {
    const { t, i18n } = useTranslation('simpleAnalytics');
    const { t: tAnalysis } = useTranslation('analysis');
    const { userInfo } = useUser();
    const languageTag = buildLanguageTagFromUser(userInfo, i18n.language);
    const { t: tCommon } = useTranslation('common');
    // ユーザー情報の取得
    const userId = userInfo?.userId;
    const isPaidPlan = userInfo?.isPaid === true;
    const [isFullscreenLockedModalOpen, setIsFullscreenLockedModalOpen] = useState(false);

    // パネルサイズに応じたフォントサイズの動的調整
    const containerRef = useRef<HTMLDivElement>(null);
    const fontSize = useResponsiveFontSize(containerRef);
    const weekStartsOn = resolveWeekStart(userInfo?.weekStart);
    const isJapanese = languageTag.startsWith('ja');
    const isSmUpViewport = useSmUpViewport();

    // データ表示用の状態管理
    const [viewModel, setViewModel] = useState<SimpleAnalyticsViewModel | null>(null);
    const [ratioChartView, setRatioChartView] = useState<'stacked' | 'donut'>('stacked');
    const [legendOpen, setLegendOpen] = useState<boolean>(false);
    const [stackedBarViewMode, setStackedBarViewMode] = useState<BarChartViewMode>('expand');
    const [transitionBarViewMode, setTransitionBarViewMode] = useState<BarChartViewMode>('fit');
    const legendAnchorRef = useRef<HTMLButtonElement | null>(null);
    // ETagはメモリにのみ保持
    const etagRef = useRef<string | null>(null);
    // 初期レンダリング時の重複フェッチを抑制
    const fetchKeyRef = useRef<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isPeriodRestored, setIsPeriodRestored] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // モーダル関連の状態管理
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [modalError, setModalError] = useState<string | null>(null);

    // フォームの状態管理（期間、色、キーワード）
    const [formState, setFormState] = useState<FormState>({
        periodType: 'LAST_WEEK',
        color: DEFAULT_SIMPLE_ANALYTICS_COLOR,
        keyword: '',
    });

    // カレンダーリスト
    const [calendarList, setCalendarList] = useState<IndividualCalendarItem[]>([]);
    // 選択されたカレンダーID
    const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');
    // プランロック用モーダル
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

    // 祝日・誕生日カレンダーを除外したカレンダーリスト
    const filteredCalendarList = useMemo(() => {
        return calendarList.filter((cal) => !isHolidayOrBirthdayCalendar(cal));
    }, [calendarList]);

    // カレンダーが1つだけかどうか
    const isSingleCalendar = filteredCalendarList.length === 1;

    // カラー選択肢の表示用状態
    const [colorOptionsView, setColorOptionsView] = useState<
        {
            value: string;
            label: string;
            colorHex?: string;
            isDivider?: boolean;
            originalLabel?: string;
        }[]
    >([]);

    const { setLoading } = usePanelLoading();

    /**
     * APIレスポンスを適用する
     * @param responseData - APIからのレスポンスデータ
     * @param nextEtag - 新しいETag（オプション）
     */
    const applyResponse = useCallback(
        async (responseData: SimpleAnalyticsResponse, nextEtag?: string | null) => {
            const vm = buildViewModel(responseData);
            setViewModel(vm);

            // フォーム状態をレスポンスデータで更新
            setFormState({
                periodType: vm.setting.periodType,
                // 色キーをそのまま反映（未設定は空文字）
                color: vm.setting.color ?? '',
                keyword: vm.setting.keyword ?? '',
            });

            // カレンダーIDを設定
            if (vm.setting.calendarId) {
                setSelectedCalendarId(String(vm.setting.calendarId));
            }

            // ETagをメモリに保持
            etagRef.current = nextEtag ?? null;

            // サーバーを正として LS の期間タイプを同期（プラン変更後の LS ズレ防止）
            try {
                if (userId) {
                    await setLocalStorage(
                        userId,
                        LocalStorageKeys.SIMPLE_ANALYTICS_PERIOD,
                        vm.setting.periodType,
                    );
                }
            } catch {
                // LS 失敗は表示には非致命
            }
        },
        [userId],
    );

    /**
     * カレンダーリストを取得
     */
    useEffect(() => {
        const fetchCalendars = async () => {
            try {
                const response = await apiGetRequest('/api/settings/calendars');
                if (!response.ok) {
                    console.error('Failed to fetch calendars:', response.status);
                    return;
                }
                const data = await response.json();
                setCalendarList(data.calendars || []);
            } catch (error) {
                console.error('Error fetching calendars:', error);
            }
        };
        void fetchCalendars();
    }, []);

    /**
     * カレンダープロバイダーに応じた色選択肢を設定する
     * MRU（Most Recently Used）ロジックで色の選択肢を並び替え
     */
    useEffect(() => {
        // 選択されたカレンダーからプロバイダー情報を取得
        const selectedCalendar = calendarList.find(
            (cal) => String(cal.id) === selectedCalendarId,
        );
        const providerId = userInfo?.calendarList?.[0]?.providerId || '';
        const providerName = selectedCalendar?.providerName || userInfo?.calendarList?.[0]?.providerName || '';
        if (!providerId) {
            setColorOptionsView([getDefaultColorOption()]);
            return;
        }
        (async () => {
            const base = await getBaseColorOptionsByProvider(providerName, languageTag);
            try {
                const key = `${LocalStorageKeys.COLOR_ORDER}:${providerId}`;
                const saved = await getLocalStorage(userId || '', key);
                const order: string[] = saved ? JSON.parse(saved) : [];
                setColorOptionsView(applyColorOrderWithDividers(base, order));
            } catch {
                setColorOptionsView(base);
            }
        })();
    }, [userInfo?.calendarList, userId, languageTag, calendarList, selectedCalendarId]);

    // 初期フェッチ前にローカルストレージから直近の期間タイプを復元し、二重フェッチを回避
    useEffect(() => {
        if (!userId) return;
        (async () => {
            try {
                const savedPeriod = await getLocalStorage(
                    userId,
                    LocalStorageKeys.SIMPLE_ANALYTICS_PERIOD,
                );
                if (savedPeriod) {
                    setFormState((prev) => ({ ...prev, periodType: savedPeriod }));
                }
            } catch {}
            setIsPeriodRestored(true);
        })();
    }, [userId]);


    const fetchData = useCallback(async () => {
        if (!userId) return;
        setIsLoading(true);
        setErrorMessage(null);

        try {
            const candidateEtag = etagRef.current || undefined;
            const response = await fetchSimpleAnalytics({
                weekStartsOn,
                etag: candidateEtag,
            });

            if (response.status === 304) {
                // 変更なし: 何も更新しない
                return;
            }

            if (!response.ok) {
                throw new Error(`status=${response.status}`);
            }

            const json = (await response.json()) as SimpleAnalyticsResponse;
            const responseEtag = response.headers.get('etag');
            await applyResponse(json, responseEtag ?? undefined);
        } catch (err) {
            console.error(err);
            setErrorMessage(t('messages.fetchFailed'));
        } finally {
            setIsLoading(false);
        }
    }, [applyResponse, t, userId, weekStartsOn]);

    useEffect(() => {
        if (!userId) {
            setIsLoading(false);
            fetchKeyRef.current = null;
            etagRef.current = null;
            return;
        }
        if (!isPeriodRestored) return;
        const fetchKey = `${userId}:${weekStartsOn}`;
        if (fetchKeyRef.current === fetchKey) {
            return;
        }
        fetchKeyRef.current = fetchKey;
        fetchData();
    }, [fetchData, userId, weekStartsOn, isPeriodRestored]);

    const resolvePeriodLabel = useCallback(
        (value?: string) => {
            if (!value) return '';
            const definition = getPeriodDefinition(value);
            if (definition) {
                return t(definition.labelKey);
            }
            return value;
        },
        [t],
    );

    const periodOptions = useMemo(
        () =>
            getPeriodOptions(isPaidPlan ?? false).map((option) => ({
                value: option.value,
                label: t(option.labelKey),
            })),
        [isPaidPlan, t],
    );

    // 選択された期間オプションを取得する
    const selectedPeriodOption = useMemo(() => {
        const found = periodOptions.find((opt) => opt.value === formState.periodType);
        return (
            found || {
                value: formState.periodType,
                label: resolvePeriodLabel(formState.periodType),
            }
        );
    }, [formState.periodType, periodOptions, resolvePeriodLabel]);

    const modalNotes = useMemo(() => {
        const notes = t('modal.notes', { returnObjects: true });
        return Array.isArray(notes) ? (notes as string[]) : [];
    }, [t]);

    const localizedColorOptions = useMemo(() => {
        return colorOptionsView.map((option) => {
            if (option.isDivider) return option;
            if (option.value === '') {
                return {
                    ...option,
                    label: t('panel.noColor'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            if (option.value === 'all') {
                return {
                    ...option,
                    label: t('panel.allColors'),
                    originalLabel: option.originalLabel ?? option.label,
                };
            }
            return {
                ...option,
                originalLabel: option.originalLabel ?? option.label,
            };
        });
    }, [colorOptionsView, t]);

    const defaultLocalizedColorOption = useMemo(() => {
        const base = getDefaultColorOption();
        return {
            ...base,
            label: t('panel.noColor'),
            originalLabel: base.originalLabel ?? base.label,
        };
    }, [t]);

    // 設定を保存する
    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        if (isSaving || !viewModel) return;

        // 「すべて」の場合はそのまま使用、それ以外はHEXカラーコードを正規化
        const isAllSelected = (formState.color || '').trim().toLowerCase() === 'all';
        // 色はアクティビティビューと同じ「色キー」を保存（'all'は特例）
        const normalizedColor = isAllSelected ? 'all' : (formState.color || '').trim();

        // 「すべて」以外の場合のみHEXパターンチェック
        // 色キーに対するバリデーションはバックエンド（@Size）で実施

        setIsSaving(true);
        setModalError(null);

        try {
            // カレンダーが1つだけの場合は自動的にそのカレンダーを使用
            const calendarIdToUse = selectedCalendarId || (() => {
                if (isSingleCalendar && filteredCalendarList[0]) {
                    return String(filteredCalendarList[0].id);
                }
                const defaultCalendar = calendarList.find((cal) => cal.isDefault) || calendarList[0];
                return defaultCalendar ? String(defaultCalendar.id) : '';
            })();

            const payload: SimpleAnalyticsUpdatePayload = {
                calendarId: calendarIdToUse || undefined,
                periodType: formState.periodType,
                color: normalizedColor,
                keyword: formState.keyword?.trim() ?? '',
            };

            // 送信
            const response = await updateSimpleAnalytics(payload);
            if (!response.ok) {
                if (response.status === 401) {
                    setModalError(t('modal.errors.sessionExpired'));
                } else {
                    const body = await response.json().catch(() => null);
                    const message = body?.message || t('modal.errors.updateFailed');
                    setModalError(message);
                }
                return;
            }

            const candidateEtag = etagRef.current || undefined;
            const refetch = await fetchSimpleAnalytics({
                weekStartsOn,
                etag: candidateEtag,
            });
            if (refetch.status !== 304) {
                if (!refetch.ok) {
                    throw new Error(`status=${refetch.status}`);
                }
                const json = (await refetch.json()) as SimpleAnalyticsResponse;
                const responseEtag = refetch.headers.get('etag');
                await applyResponse(json, responseEtag ?? undefined);
            } else {
                // 304 時は applyResponse が走らないため、保存した期間だけ LS を同期
                try {
                    if (userId) {
                        await setLocalStorage(
                            userId,
                            LocalStorageKeys.SIMPLE_ANALYTICS_PERIOD,
                            formState.periodType,
                        );
                    }
                } catch {}
            }

            // イベントログ送信（登録成功後）
            void logSimpleAnalysisEvent({
                periodType: formState.periodType,
                color: normalizedColor,
                hasKeyword: Boolean(formState.keyword?.trim()),
            });
            
            setIsModalOpen(false);
            setModalError(null);
        } catch (error) {
            console.error(error);
            setModalError(t('modal.errors.updateFailed'));
        } finally {
            setIsSaving(false);
        }
    };

    // --- 比率: 積み上げデータ構築・色解決・ツールチップ ---
    const formatStackedDuration = useCallback((minutes: number) => {
        const [hours, mins] = getHoursAndMinutes(minutes);
        const parts: string[] = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
        return parts.join('');
    }, []);

    const stackedAxisTickFormatter = useCallback(
        (value: number) => formatStackedDuration(Math.round(value)),
        [formatStackedDuration],
    );

    const stackedTooltipLabelFormatter = useCallback<StackedVerticalBarChartTooltipLabelFormatter>(
        ({ label, value }) => {
            const minutes = Math.round(value);
            return `${label?.trim() || t('panel.untitled', { defaultValue: 'Untitled' })}: ${formatStackedDuration(minutes)}`;
        },
        [formatStackedDuration, t],
    );

    const formatMonthAwareLabel = useCallback(
        (currentDate: string, previousDate?: string) => {
            if (!currentDate) return '';
            const curNorm = currentDate.includes('/')
                ? currentDate.replace(/\//g, '-')
                : currentDate;
            const prevNorm = previousDate
                ? previousDate.includes('/')
                    ? previousDate.replace(/\//g, '-')
                    : previousDate
                : undefined;
            const curMonth = curNorm.split('-')[1];
            const prevMonth = prevNorm?.split('-')[1];
            const trimmed = removeZeroPaddingFromDate(removeYearFromDate(curNorm));
            const [m, d] = trimmed.split('-');
            const base = !prevMonth || prevMonth !== curMonth ? `${m}/${d}` : d;
            const weekday = getWeekday(curNorm, i18n.language);
            if (!weekday) return base.replace(/-/g, '/');
            const isJa = i18n.language?.startsWith('ja');
            const shortWeekday = isJa ? weekday.slice(0, 1) : weekday.slice(0, 2);
            const display = base.replace(/-/g, '/');
            return `${display}(${shortWeekday})`;
        },
        [i18n.language],
    );

    const stackedChartConfig = useMemo(() => {
        const labelCount = viewModel?.ratioData?.dailyAggregatedData?.length ?? 0;
        return getChartDisplayConfig('ratio', labelCount, isSmUpViewport, stackedBarViewMode);
    }, [viewModel?.ratioData?.dailyAggregatedData?.length, isSmUpViewport, stackedBarViewMode]);

    const transitionChartConfig = useMemo(() => {
        const labelCount = viewModel?.transitionData?.transitionEventItems?.length ?? 0;
        return getChartDisplayConfig('transition', labelCount, isSmUpViewport, transitionBarViewMode);
    }, [viewModel?.transitionData?.transitionEventItems?.length, isSmUpViewport, transitionBarViewMode]);

    const stackedChartData = useMemo<StackedVerticalBarChartData | null>(() => {
        const daily = viewModel?.ratioData?.dailyAggregatedData ?? [];
        if (!daily.length) return null;
        const usePrevDay = stackedChartConfig.useMonthAwareLabels;
        const labels = daily.map((d, idx) =>
            formatMonthAwareLabel(
                d.date,
                usePrevDay && idx > 0 ? daily[idx - 1].date : undefined,
            ),
        );
        const length = daily.length;
        const datasetMap = new Map<
            string,
            { dataset: StackedVerticalBarChartDataset; totalMinutes: number }
        >();
        daily.forEach((day, dayIndex) => {
            day.eventItems.forEach((event) => {
                const rawKey = event.title ?? '';
                if (!datasetMap.has(rawKey)) {
                    datasetMap.set(rawKey, {
                        dataset: {
                            id: rawKey,
                            label:
                                rawKey.trim().length > 0
                                    ? rawKey
                                    : t('panel.untitled', { defaultValue: 'Untitled' }),
                            values: Array(length).fill(0),
                        },
                        totalMinutes: 0,
                    });
                }
                const holder = datasetMap.get(rawKey);
                if (!holder) return;
                const minutes = Number(event.totalTime ?? 0);
                holder.dataset.values[dayIndex] = minutes;
                holder.totalMinutes += minutes;
            });
        });
        const datasets = Array.from(datasetMap.values())
            .filter((entry) => entry.totalMinutes > 0)
            .sort((a, b) => b.totalMinutes - a.totalMinutes)
            .map((entry) => entry.dataset);
        if (!datasets.length) return null;
        const totals = daily.map((d) => Number(d.totalTime ?? 0));
        return { labels, totals, datasets };
    }, [formatMonthAwareLabel, t, viewModel?.ratioData?.dailyAggregatedData, stackedChartConfig.useMonthAwareLabels]);

    const stackedYAxisSuggestedMax = useMemo(() => {
        if (!stackedChartData?.totals?.length) return undefined;
        const max = Math.max(...stackedChartData.totals);
        if (!Number.isFinite(max) || max <= 0) return undefined;
        return max * 1.1;
    }, [stackedChartData]);

    const colorCacheRef = useRef<{ language: string; map: Map<string, string> } | null>(null);
    const resolveStackedColor = useCallback(
        async (label: string) => {
            const key = label?.trim();
            if (!key) return undefined;
            const currentLanguage = i18n.language;
            if (!colorCacheRef.current || colorCacheRef.current.language !== currentLanguage) {
                try {
                    const colorDefinitions = await getColors('all', currentLanguage);
                    const map = new Map<string, string>();
                    colorDefinitions.forEach((color) => {
                        if (color.label) map.set(color.label, color.hexColor);
                        if (color.originalLabel) map.set(color.originalLabel, color.hexColor);
                    });
                    colorCacheRef.current = { language: currentLanguage, map };
                } catch {
                    colorCacheRef.current = { language: currentLanguage, map: new Map() };
                }
            }
            return colorCacheRef.current?.map.get(key);
        },
        [i18n.language],
    );

    // フックは早期returnより前に宣言して順序を固定する
    // previousTransition は直接 NormalVerticalBarChart に渡すため派生値は保持しない
    // 期間比較を計算する
    const formatDuration = useCallback(
        (minutes: number) => {
            if (!minutes || minutes <= 0) {
                return t('panel.totalZero');
            }
            const [hours, mins] = getHoursAndMinutes(minutes);
            if (!hours && !mins) {
                return t('panel.totalZero');
            }
            if (!isJapanese) {
                return getEnglishHoursAndMinutes([hours, mins]);
            }
            const parts: string[] = [];
            if (hours > 0) {
                parts.push(tCommon('time.hours', { count: hours }));
            }
            if (mins > 0) {
                parts.push(tCommon('time.minutes', { count: mins }));
            }
            const joiner = tCommon('time.joiner', {
                defaultValue: '',
            });
            return parts.join(joiner);
        },
        [isJapanese, t, tCommon],
    );

    const totalComparison = useMemo(() => {
        const prev = Number((viewModel as any)?.previousPeriodTotalMinutes || 0);
        const curr = Number((viewModel as any)?.totalMinutes || 0);
        if (!isPaidPlan || prev <= 0) return undefined;
        return calculatePeriodComparison(curr, prev);
    }, [isPaidPlan, viewModel]);

    useEffect(() => {
        setLoading(isLoading);
    }, [isLoading, setLoading]);

    if (isLoading) {
        return <div className="h-full w-full" />;
    }

    if (errorMessage) {
        return (
            <div className="h-full w-full flex items-center justify-center text-center text-red-500">
                <p>{errorMessage}</p>
            </div>
        );
    }

    if (!viewModel) {
        return (
            <div className="h-full w-full flex items-center justify-center text-center">
                <p>{t('messages.noData')}</p>
            </div>
        );
    }

    const { setting, chartType, totalMinutes, ratioData, transitionData } = viewModel;
    const [totalHours, totalMins] = getHoursAndMinutes(totalMinutes);

    return (
        <div
            ref={containerRef}
            style={{ fontSize: `${fontSize}px` }}
            className="h-full w-full flex flex-col"
        >
            {/* 検索条件表示 */}
            <div className="relative px-2 pt-2 pb-3">
                <div className="text-gray-600 flex flex-wrap items-center justify-center gap-3">
                    {/* 期間 */}
                    <span className="text-gray-800">{resolvePeriodLabel(setting.periodType)}</span>
                    {/* 色 */}
                    {setting.color && (
                        <>
                            <span className="text-gray-300">|</span>
                            <span className="inline-flex items-center gap-1">
                                {(setting.color || '').trim().toLowerCase() !== 'all' &&
                                    (() => {
                                        const opt = colorOptionsView.find(
                                            (o) => o.value === setting.color,
                                        );
                                        const hex = (opt?.colorHex || '').toUpperCase();
                                        return (
                                            <span
                                                className="inline-block border border-gray-200 rounded-sm"
                                                style={{
                                                    backgroundColor: hex || 'transparent',
                                                    width: '0.8em',
                                                    height: '0.8em',
                                                }}
                                            />
                                        );
                                    })()}
                                <span>
                                    {(setting.color || '').trim().toLowerCase() === 'all'
                                        ? t('panel.allColors')
                                        : (() => {
                                              const localized = localizedColorOptions.find(
                                                  (o) => o.value === setting.color,
                                              );
                                              const opt = colorOptionsView.find(
                                                  (o) => o.value === setting.color,
                                              );
                                              return (
                                                  localized?.label || opt?.label || setting.color
                                              );
                                          })()}
                                </span>
                            </span>
                        </>
                    )}
                    {/* キーワード */}
                    {setting.keyword && (
                        <>
                            <span className="text-gray-300">|</span>
                            <span className="inline-flex items-center gap-1">
                                <span
                                    className="text-gray-800 truncate"
                                    style={{ maxWidth: '12em' }}
                                    title={setting.keyword}
                                >
                                    {setting.keyword}
                                </span>
                            </span>
                        </>
                    )}
                </div>
                {/* 検索条件の下の線（テキスト幅に合わせる） */}
                <div className="flex justify-center mt-2">
                    <div className="h-px bg-gray-200" style={{ width: 'fit-content' }}>
                        <div className="h-full bg-gray-200" style={{ width: '100%' }}></div>
                    </div>
                </div>
                {/* メニューボタン */}
                <div className="absolute top-0 right-0 flex items-center gap-2">
                    {(() => {
                        const isRatio = chartType === 'ratio';
                        const canShowLegend = isRatio && ratioChartView === 'stacked';

                        const barViewMenuItem: PanelMenuItem | null =
                            isRatio &&
                            ratioChartView === 'stacked' &&
                            stackedChartConfig.showViewModeMenu
                                ? buildBarChartViewModeMenuItem({
                                      mode: stackedBarViewMode,
                                      onChange: setStackedBarViewMode,
                                      labelExpand: tAnalysis('chart.viewMode.expand'),
                                      labelFit: tAnalysis('chart.viewMode.fit'),
                                      suppressNextFitAction: !isSmUpViewport,
                                  })
                                : !isRatio &&
                                  transitionChartConfig.showViewModeMenu
                                  ? buildBarChartViewModeMenuItem({
                                        mode: transitionBarViewMode,
                                        onChange: setTransitionBarViewMode,
                                        labelExpand: tAnalysis('chart.viewMode.expand'),
                                        labelFit: tAnalysis('chart.viewMode.fit'),
                                    })
                                  : null;

                        const baseItems: PanelMenuItem[] = [
                            ...(barViewMenuItem ? [barViewMenuItem] : []),
                            {
                                key: 'legend',
                                label: t('analysis:chart.legendColors', {
                                    defaultValue: '項目表示',
                                }),
                                icon: <LegendIcon />,
                                onClick: canShowLegend ? () => setLegendOpen((v) => !v) : undefined,
                                disabled: !canShowLegend,
                            },
                            {
                                key: 'toggle-view',
                                label:
                                    ratioChartView === 'stacked'
                                        ? t('panel.switch.toDonut', {
                                              defaultValue: 'ドーナツ表示',
                                          })
                                        : t('panel.switch.toStacked', {
                                              defaultValue: '積み上げ表示',
                                          }),
                                icon:
                                    ratioChartView === 'stacked' ? <DonutIcon /> : <StackedIcon />,
                                onClick:
                                    ratioChartView === 'stacked'
                                        ? () => {
                                              setLegendOpen(false);
                                              setRatioChartView('donut');
                                          }
                                        : () => setRatioChartView('stacked'),
                                disabled: !isRatio,
                            },
                            // 編集ボタン（非全画面時のみ表示）
                            ...(isFullScreen
                                ? []
                                : [
                                      {
                                          key: 'edit',
                                          label: t('panel.edit.title'),
                                          icon: <EditIcon />,
                                          onClick: () => {
                                              setModalError(null);
                                              // 既存のカレンダーIDを設定
                                              if (viewModel?.setting.calendarId) {
                                                  setSelectedCalendarId(String(viewModel.setting.calendarId));
                                              } else if (isSingleCalendar && filteredCalendarList[0]) {
                                                  // カレンダーが1つだけの場合は自動的にそのカレンダーを設定
                                                  setSelectedCalendarId(String(filteredCalendarList[0].id));
                                              } else {
                                                  // デフォルトカレンダーを設定
                                                  const defaultOption = getDefaultIndividualCalendarOption(calendarList);
                                                  setSelectedCalendarId(defaultOption.value);
                                              }
                                              setIsModalOpen(true);
                                          },
                                      } as PanelMenuItem,
                                  ]),
                        ].filter((it) => !it.disabled);

                        const menuItems = buildPanelMenuItems({
                            baseItems,
                            enableFullscreen: Boolean(onEnterFullScreen),
                            isFullScreen,
                            onEnterFullScreen: isPaidPlan ? onEnterFullScreen : undefined,
                            onExitFullScreen,
                            enterLabel: tCommon('actions.fullscreenEnter'),
                            exitLabel: tCommon('actions.fullscreenExit'),
                            isFullscreenLocked: !isPaidPlan,
                            fullscreenTooltip: tCommon('actions.fullscreenLockedTooltip'),
                            onFullscreenLockedClick: () => setIsFullscreenLockedModalOpen(true),
                        });

                        return (
                            <HoverMenuButton
                                title={t('panel.menu')}
                                ariaLabel={t('panel.menu')}
                                anchorRef={
                                    legendAnchorRef as unknown as React.RefObject<HTMLDivElement | null>
                                }
                                items={menuItems}
                            />
                        );
                    })()}
                </div>
            </div>

            {/* 合計とチャートのブロック */}
            <div className="flex-1 min-h-0 flex flex-col">
                {/* 合計表示 */}
                <div className="px-2 flex items-end">
                    <div className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-0 w-full">
                        {/* 合計ラベル + 値（左側・折り返さない） */}
                        <div className="flex items-baseline gap-2 whitespace-nowrap">
                            <span className="text-gray-600" style={{ fontSize: '0.8em' }}>
                                {t('panel.totalLabel')}
                            </span>
                            <span className="tracking-wide text-gray-800 flex items-end gap-2 whitespace-nowrap">
                                {isJapanese ? (
                                    <span style={{ fontSize: '1.4em', lineHeight: '1' }}>
                                        {formatDuration(totalMinutes)}
                                    </span>
                                ) : (
                                    <>
                                        {totalHours > 0 && (
                                            <span className="flex items-end gap-1">
                                                <span
                                                    style={{ fontSize: '1.4em', lineHeight: '1' }}
                                                >
                                                    {totalHours}
                                                </span>
                                                <span
                                                    className="text-gray-500"
                                                    style={{
                                                        fontSize: '1em',
                                                        lineHeight: '1',
                                                        marginLeft: '0.1em',
                                                    }}
                                                >
                                                    h
                                                </span>
                                            </span>
                                        )}
                                        <span className="flex items-end gap-1">
                                            <span style={{ fontSize: '1.4em', lineHeight: '1' }}>
                                                {totalMins}
                                            </span>
                                            <span
                                                className="text-gray-500"
                                                style={{
                                                    fontSize: '1em',
                                                    lineHeight: '1',
                                                    marginLeft: '0.1em',
                                                }}
                                            >
                                                m
                                            </span>
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                        {/* 前期比較（右側・折り返さない） */}
                        {totalComparison && (
                            <div className="flex-shrink-0 whitespace-nowrap">
                                <PeriodComparison
                                    comparison={totalComparison}
                                    valueType="time"
                                    className="text-[0.9em]"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* チャート表示 */}
                <div className="flex-1 min-h-0">
                    <div className="w-full h-full overflow-visible ps-2 pb-2 pe-2">
                        {chartType === 'ratio' ? (
                            ratioChartView === 'stacked' ? (
                                <StackedVerticalBarChart
                                    data={
                                        stackedChartData ?? {
                                            labels: [],
                                            datasets: [],
                                        }
                                    }
                                    resolveColor={resolveStackedColor}
                                    tooltipLabelFormatter={stackedTooltipLabelFormatter}
                                    yAxisSuggestedMax={stackedYAxisSuggestedMax}
                                    formatYAxisTick={stackedAxisTickFormatter}
                                    onSwitchToDonut={() => setRatioChartView('donut')}
                                    hideSwitchButton={true}
                                    hideLegendButton={true}
                                    legendOpen={legendOpen}
                                    onToggleLegend={() => setLegendOpen((v) => !v)}
                                    legendAnchorRef={
                                        legendAnchorRef as unknown as React.RefObject<HTMLElement | null>
                                    }
                                    barViewMode={stackedBarViewMode}
                                />
                            ) : (
                                <DonutChart
                                    data={ratioData ?? EMPTY_RATIO}
                                    onSwitchToStacked={() => setRatioChartView('stacked')}
                                    hideSwitchButton={true}
                                />
                            )
                        ) : (
                            <NormalVerticalBarChart
                                data={transitionData?.transitionEventItems ?? []}
                                previousData={
                                    isPaidPlan && viewModel.previousTransition?.dateAndTimeList
                                        ? (viewModel.previousTransition.dateAndTimeList as any)
                                        : undefined
                                }
                                barViewMode={transitionBarViewMode}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* 未設定ガイダンス（最下部表示） */}
            {!setting.id && (
                <div className="px-2 pt-2 pb-3 text-center text-gray-500 text-[0.7em]">
                    {t('panel.defaultNotice.line1')}
                    <br />
                    {t('panel.defaultNotice.line2')}
                </div>
            )}

            {/*
             * モーダル
             */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setModalError(null);
                }}
                contentClassName="w-full max-w-lg p-6"
            >
                <form onSubmit={handleSave}>
                    <h3 className="text-[1.125em] font-semibold mb-4 text-center">
                        {t('modal.title')}
                    </h3>

                    {/* 祝日カレンダーを除外してカレンダーが1つだけの場合は非表示 */}
                    {!isSingleCalendar && (
                        <div className="mb-6">
                            <Select
                                label={t('modal.labels.calendar')}
                                options={createIndividualCalendarOptions(calendarList, { lockNonDefault: !isPaidPlan })}
                                defaultValue={(() => {
                                    if (!selectedCalendarId) {
                                        const defaultCalendar = calendarList.find((cal) => cal.isDefault) || calendarList[0];
                                        return defaultCalendar ? { value: String(defaultCalendar.id), label: defaultCalendar.calendarName } : { value: '', label: '' };
                                    }
                                    const calendar = calendarList.find((cal) => String(cal.id) === selectedCalendarId);
                                    return calendar ? { value: String(calendar.id), label: calendar.calendarName } : { value: selectedCalendarId, label: selectedCalendarId };
                                })()}
                                onValueChange={(option) => {
                                    if (option.isLocked) {
                                        setIsPlanModalOpen(true);
                                        return;
                                    }
                                    setSelectedCalendarId(option.value);
                                }}
                                className="h-8 min-w-[200px]"
                                noWrap={true}
                            />
                        </div>
                    )}

                    <div className="mb-6">
                        <Select
                            label={t('modal.labels.period')}
                            options={periodOptions}
                            defaultValue={selectedPeriodOption}
                            onValueChange={(opt) =>
                                setFormState((prev) => ({ ...prev, periodType: opt.value }))
                            }
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-[0.875em] font-medium text-gray-700 mb-1">
                            {t('modal.labels.color')}
                            <span className="text-gray-400"> {t('modal.optional')}</span>
                        </label>
                        <Select
                            options={localizedColorOptions}
                            defaultValue={(() => {
                                const key = (formState.color || '').trim();
                                if (!key) {
                                    return (
                                        localizedColorOptions.find((o) => o.value === '') ||
                                        defaultLocalizedColorOption
                                    );
                                }
                                if (key.toLowerCase() === 'all') {
                                    return (
                                        localizedColorOptions.find((o) => o.value === 'all') ||
                                        defaultLocalizedColorOption
                                    );
                                }
                                const foundByValue = localizedColorOptions.find(
                                    (o) => o.value === key,
                                );
                                return foundByValue || defaultLocalizedColorOption;
                            })()}
                            onValueChange={(option: any) => {
                                if (option?.isDivider) return;

                                // 「すべて」選択時は"all"、それ以外は色キー（value）を保存
                                const colorValue =
                                    option?.value === 'all' ? 'all' : (option?.value || '').trim();

                                setFormState((prev) => ({
                                    ...prev,
                                    color: colorValue,
                                    // 「すべて」選択時はキーワードをクリア
                                    keyword: option?.value === 'all' ? '' : prev.keyword,
                                }));

                                // MRU保存
                                const v = option?.value;
                                const providerId = userInfo?.calendarList?.[0]?.providerId || '';
                                if (v && v !== 'all' && providerId) {
                                    (async () => {
                                        try {
                                            const key = `${LocalStorageKeys.COLOR_ORDER}:${providerId}`;
                                            const saved = await getLocalStorage(userId || '', key);
                                            let order: string[] = saved ? JSON.parse(saved) : [];
                                            order = [v, ...order.filter((x) => x !== v)].slice(
                                                0,
                                                20,
                                            );
                                            await setLocalStorage(
                                                userId || '',
                                                key,
                                                JSON.stringify(order),
                                            );
                                            const base = await getBaseColorOptionsByProvider(
                                                userInfo?.calendarList?.[0]?.providerName || '',
                                                languageTag,
                                            );
                                            setColorOptionsView(
                                                applyColorOrderWithDividers(base, order),
                                            );
                                        } catch {}
                                    })();
                                }
                            }}
                            className="text-[1em] w-full min-w-[250px]"
                            dropdownClassName="w-full"
                        />
                    </div>

                    <div className="mb-6">
                        <label className="block text-[0.875em] font-medium text-gray-700 mb-1">
                            {t('modal.labels.keyword')}
                            <span className="text-gray-400"> {t('modal.optional')}</span>
                        </label>
                        <Input
                            type="text"
                            value={formState.keyword}
                            maxLength={255}
                            placeholder={
                                (formState.color || '').trim().toLowerCase() === 'all'
                                    ? t('modal.placeholders.keywordAll')
                                    : t('modal.placeholders.keywordDefault')
                            }
                            showClearButton={true}
                            disabled={(formState.color || '').trim().toLowerCase() === 'all'}
                            onChange={(e) =>
                                setFormState((prev) => ({
                                    ...prev,
                                    keyword: e.target.value,
                                }))
                            }
                        />
                        <p className="text-[0.75em] text-gray-400 mt-1">{t('modal.helper')}</p>
                    </div>

                    {modalError && (
                        <p className="text-[0.875em] text-red-500 mb-3 whitespace-pre-wrap">
                            {modalError}
                        </p>
                    )}

                    <ul className="list-disc list-inside py-6">
                        {modalNotes.map((note, index) => (
                            <li key={index} className="text-[0.8em] text-gray-500 text-center mb-2">
                                {note}
                            </li>
                        ))}
                    </ul>

                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-12 py-4 justify-center w-full">
                        <Button
                            type="button"
                            className="w-full sm:w-auto px-6 py-2 h-10 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 whitespace-nowrap"
                            onClick={() => {
                                setIsModalOpen(false);
                                setModalError(null);
                            }}
                        >
                            {t('modal.buttons.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            className="w-full sm:w-auto px-6 py-2 h-10 bg-kotomilThemaColor hover:bg-kotomilThemaColor/80 disabled:opacity-60 whitespace-nowrap"
                            disabled={isSaving}
                        >
                            {isSaving ? t('modal.buttons.submitting') : t('modal.buttons.submit')}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* フルスクリーンロック案内モーダル */}
            {!isPaidPlan && (
                <FullscreenLockedModal
                    isOpen={isFullscreenLockedModalOpen}
                    onClose={() => setIsFullscreenLockedModalOpen(false)}
                    onGoPlans={() => {
                        openPlansWithTracking({
                            source: 'fullscreen_simpleAnalytics',
                            panelId: 'hmSimpleAnalytics',
                        });
                    }}
                />
            )}

            {/* カレンダー選択ロック案内モーダル */}
            {!isPaidPlan && (
                <PlanLockedModal
                    isOpen={isPlanModalOpen}
                    onClose={() => setIsPlanModalOpen(false)}
                    onGoPlans={() => {
                        openPlansWithTracking({
                            source: 'calendar_selection_simple_analytics',
                            panelId: 'hmSimpleAnalytics',
                        });
                    }}
                />
            )}
        </div>
    );
};

interface PlanLockedModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGoPlans: () => void;
}

function PlanLockedModal({ isOpen, onClose, onGoPlans }: PlanLockedModalProps) {
    const { t } = useTranslation('analysis');
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            contentClassName="w-full max-w-md p-6"
        >
            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center">
                    {t('lockedModal.title')}
                </h3>
                <div
                    className="text-sm text-gray-700 [&_strong]:font-semibold [&_strong]:block [&_strong]:mt-3 [&_strong]:mb-2 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:mt-2 [&_ul]:mb-2 [&_ul]:pl-4 [&_li]:mb-1 [&_li]:leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: t('lockedModal.message') }}
                />
                <div className="flex justify-center mt-4">
                    <button
                        type="button"
                        className="px-6 py-2 rounded-md bg-kotomilThemaColor text-black hover:bg-kotomilThemaColor/90 text-sm inline-flex items-center gap-2"
                        onClick={() => {
                            onClose();
                            onGoPlans();
                        }}
                    >
                        <span>{t('lockedModal.cta')}</span>
                        <ExternalLinkIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default SimpleAnalyticsPanel;
